import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon'
type ButtonSize = 'sm' | 'md'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
}

function getVariantStyle(variant: ButtonVariant) {
  if (variant === 'primary') {
    return {
      background: 'var(--accent)',
      borderColor: 'var(--accent)',
      color: '#fff',
    }
  }

  if (variant === 'danger') {
    return {
      background: 'var(--color-danger-bg)',
      borderColor: 'var(--color-danger-border)',
      color: 'var(--color-danger)',
    }
  }

  if (variant === 'ghost') {
    return {
      background: 'transparent',
      borderColor: 'transparent',
      color: 'var(--text-muted)',
    }
  }

  return {
    background: 'var(--bg-card)',
    borderColor: 'var(--border)',
    color: 'var(--text)',
  }
}

function getSizeClass(size: ButtonSize, variant: ButtonVariant) {
  const height = size === 'sm' ? 'h-[30px]' : 'h-[34px]'
  const width = variant === 'icon' ? (size === 'sm' ? 'w-[30px]' : 'w-[34px]') : ''
  const padding = variant === 'icon' ? 'px-0' : size === 'sm' ? 'px-2.5' : 'px-3'
  return `${height} ${width} ${padding}`
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  className = '',
  style,
  ...props
}: ButtonProps, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md border text-[11px] font-medium transition-colors disabled:cursor-default disabled:opacity-45 ${getSizeClass(size, variant)} ${className}`.trim()}
      style={{
        ...getVariantStyle(variant),
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  )
})
